///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

import React, { useState, useEffect } from 'react'
import Layouts from './MainAppComponents/Layouts'
import Profile from './MainAppComponents/Profile'
import Subscriptions from './MainAppComponents/Subscriptions'
import { useAuth } from '../context/AuthContext';
import Companies from './MainAppComponents/Companies';
import { createNotification } from "../services/NotificationService"
import Invoices from './MainAppComponents/Invoices';
import ImportExcel from './MainAppComponents/ImportExcel';
import {
    isStillActive,
    subscriptionNames,
    subscriptionExpireDate,
    currentLinesAvailable,
    daysUntilExpiry,
    isLastSubMonth
} from '../services/SubscriptionsService';
import { formatNr } from '../services/FormatService';
import ConfirmBox from './ConfirmBox';
import IntrastatValidator from './IntrastatValidator';
import AdminPanel from './MainAppComponents/AdminComponents/AdminPanel';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faSpinner,
    faMagnifyingGlass,
    faFileExport,
    faIdCard,
    faChartLine,
    faBoxesStacked,
    faClockRotateLeft,
    faLayerGroup,
    faUser,
    faCreditCard,
    faReceipt,
    faBuilding,
    faGear,
    faBook,
    faTriangleExclamation,
    faFileCode,
    faLock
} from '@fortawesome/free-solid-svg-icons'
import ReactTooltip from 'react-tooltip';
import ModalSetValue from './MainAppComponents/ModalSetValue';
import GeneratedHistory from './MainAppComponents/GeneratedHistory';
import { useNavigate } from "react-router-dom";

const MainApp = () => {
    const navigate = useNavigate();

    const goToPage = (page) => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        navigate('/' + page);
    };

    const [modal, setModal] = useState(false)
    const [page, setPage] = useState('')
    const [openConfirm, setOpenConfirm] = useState(false)
    const [confirmData, setConfirmData] = useState({})
    const [windowOpen, setWindowOpen] = useState(false)
    const {
        userData,
        loading,
        layouts,
        cardPaymentsAvailable,
    } = useAuth()

    // Check if user data is fully loaded (userData exists and has subscriptions array)
    const isDataReady = !loading && userData && Array.isArray(userData.subscriptions)

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [loading, windowOpen, isDataReady])


    const notWithoutValid = new Set(['import', 'companies', 'layouts'])

    // Get subscription data for status cards
    const sub = userData?.subscriptions?.[0]
    const hasSubscription = sub !== undefined
    //? `valid` is what actually unlocks the program - it is set only once the payment is
    //? confirmed. isStillActive() only answers "has it expired", so on its own it reports a
    //? brand new unpaid subscription as active, and the dashboard used to show full lines
    //? and a green "Activ" badge for something the user could not use yet.
    const subIsPending = hasSubscription && !sub.valid && !sub.manualDisable
    const subIsActive = hasSubscription && sub.valid && isStillActive(sub.date, sub.duration, sub.manualDisable)
    const subTierName = hasSubscription ? subscriptionNames(sub.name) : 'Fără abonament'
    const subExpiryDate = hasSubscription ? subscriptionExpireDate(sub.date, sub.duration) : '-'
    //? clamped to the last bucket: on the expiry day currentSubMonth equals the duration,
    //? one past the end of sub.lines, which used to display an active subscription as
    //? having 0 lines left
    const remainingRaw = hasSubscription ? currentLinesAvailable(sub) : NaN
    const linesRemaining = isNaN(remainingRaw) ? 0 : remainingRaw
    //? the subscription is on its final month - the user has to renew to keep going
    const subInLastMonth = subIsActive && isLastSubMonth(sub)
    const subDaysLeft = subInLastMonth ? daysUntilExpiry(sub.date, sub.duration) : 0
    const linesTotal = hasSubscription ? sub.linesStart ?? 0 : 0
    const linesPercentRemaining = linesTotal > 0 ? (linesRemaining / linesTotal) * 100 : 0
    const layoutsAvailable = hasSubscription ? sub.layoutsAvailable ?? 0 : 0
    const companiesAvailable = hasSubscription ? sub.compAvailable ?? 1 : 1

    const handleOpen = async (window, amSure = false) => {
        if(loading || !isDataReady) return

        if (layouts && window === "import") {
            if (layouts.length === 0) {
                createNotification('warning', 'Aveți nevoie de cel puțin o machetă creată pentru a putea genera Intrastat')
                return;
            }
        }

        let toOpen = window
        amSure && setOpenConfirm(false)

        if(page === 'import' && !amSure) {
            let messageToDisplay = "Sigur doriți să părăsiți pagina? Progresul va fi pierdut."
            if (toOpen === 'import') {
                messageToDisplay += " Nu uitați să salvați modificările făcute local în fișierul Excel înainte de a îl reîncărca pentru a fi vizibile în aplicație."
            }
            setConfirmData({
                content: messageToDisplay,
                title: 'Confirmați',
                buttonConfirm: 'Confirmă',
                buttonCancel: 'Anulează',
                func: () => handleOpen(window, true)
            })
            setOpenConfirm(true)
            return
        }

        // check if user has any subscription
        const hasNoSub = userData.subscriptions.length === 0
        if (notWithoutValid.has(window) && hasNoSub) {
            createNotification('info', 'Nu aveți niciun abonament selectat.')
            return
        }

        //? Do not just refuse: send them where the problem can actually be solved.
        if (notWithoutValid.has(window) && !userData.subscriptions[0].valid) {
            createNotification('info',
                cardPaymentsAvailable
                    ? 'Abonamentul se activează după confirmarea plății. Alegeți modalitatea de plată mai jos.'
                    : 'Abonamentul se activează după confirmarea plății. Datele de plată se găsesc mai jos.',
                'Abonament neactivat', 12000)
            setWindowOpen(true)
            setPage('invoices')
            return
        }

        // If clicking the same page, close it
        if(page === toOpen) {
            setWindowOpen(false);
            setPage('');
            return
        }

        //? 'validator' is free for everyone, expired subscription included - it degrades to
        //? the visitor tier on its own and is a better renewal prompt than a hard refusal
        if(toOpen !== 'subscriptions'
            && toOpen !== 'invoices'
            && toOpen !== 'admin-panel'
            && toOpen !== 'validator'
            && toOpen !== 'profile') {
            const goToSub = () => {
                setWindowOpen(true);
                setPage('subscriptions');
            }
            if (hasNoSub) {
                goToSub()
                return createNotification('error', 'Alegeți un abonament pentru a beneficia de program.')
            }
            if(!isStillActive(userData.subscriptions[0].date,
                userData.subscriptions[0].duration,
                userData.subscriptions[0].manualDisable)) {
                    goToSub()
                    return createNotification('error', `Abonamentul actual este expirat.
                    Selectați unul nou pentru a beneficia de program.`)
            }
        }

        setWindowOpen(true);
        setPage(toOpen);

        ReactTooltip.rebuild();
    }

    const handleClose = () => {
        if(page === 'import') {
            setConfirmData({
                content: "Sigur doriți să părăsiți pagina? Progresul va fi pierdut.",
                title: 'Confirmați',
                buttonConfirm: 'Confirmă',
                buttonCancel: 'Anulează',
                func: () => {
                    setOpenConfirm(false)
                    setWindowOpen(false)
                    setPage('')
                }
            })
            setOpenConfirm(true)
            return
        }
        setWindowOpen(false)
        setPage('')
    }

    // Render dashboard when no content is open
    const renderDashboard = () => (
        <div className='dashboard-container'>
            {/* Waiting for payment - the program stays locked until the payment is confirmed,
                so say so plainly here instead of letting the user find out on click */}
            {subIsPending && (
                <div className='dashboard-alert'>
                    <FontAwesomeIcon icon={faTriangleExclamation} className='dashboard-alert-icon' />
                    <div className='dashboard-alert-body'>
                        <div className='dashboard-alert-title'>
                            Abonamentul {subTierName} așteaptă plata
                        </div>
                        <div className='dashboard-alert-text'>
                            Comanda a fost înregistrată, dar generarea declarațiilor se deblochează
                            abia după confirmarea plății.{' '}
                            {cardPaymentsAvailable
                                ? `Plata cu cardul activează abonamentul imediat; prin transfer
                                   bancar, activarea are loc după confirmarea încasării
                                   (de regulă 1-2 zile lucrătoare).`
                                : `Activarea are loc după confirmarea încasării, de regulă în
                                   1-2 zile lucrătoare de la efectuarea plății.`}
                        </div>
                    </div>
                    <button className='dashboard-alert-btn' onClick={() => handleOpen('invoices')}>
                        Finalizează plata
                    </button>
                </div>
            )}

            {/* Hero Card - Main CTA */}
            <div className='dashboard-hero'>
                {subIsPending ? (
                    <div className='hero-card hero-card--locked' onClick={() => handleOpen('invoices')}>
                        <FontAwesomeIcon icon={faLock} className='hero-icon' />
                        <span>Activați abonamentul pentru a genera</span>
                    </div>
                ) : (
                    <div className='hero-card' onClick={() => handleOpen('import')}>
                        <FontAwesomeIcon icon={faFileExport} className='hero-icon' />
                        <span>Generează Intrastat</span>
                    </div>
                )}
            </div>

            {/* Status Cards Row */}
            <div className='dashboard-status-row'>
                {/* Subscription Status Card */}
                <div className='status-card'>
                    <div className='status-card-header'>
                        <FontAwesomeIcon icon={faIdCard} className='status-icon' />
                        <span>Abonament</span>
                    </div>
                    <div className='status-card-body'>
                        <div className='status-tier'>{subTierName}</div>
                        <div className={`status-badge ${
                            subIsActive ? 'active' : subIsPending ? 'pending' : hasSubscription ? 'inactive' : 'warning'
                        }`}>
                            {subIsActive ? 'Activ'
                                : subIsPending ? 'În așteptarea plății'
                                : hasSubscription ? 'Expirat' : 'Neselectat'}
                        </div>
                        {/* An expiry date on something that has not started yet reads as a
                            promise it does not make - the period runs from activation */}
                        {hasSubscription && !subIsPending && (
                            <div className='status-expiry'>Expiră: {subExpiryDate}</div>
                        )}
                        {subIsPending && (
                            <div
                                className='status-renew'
                                onClick={() => handleOpen('invoices')}
                                title='Mergi la secțiunea Plăți'
                            >
                                <FontAwesomeIcon icon={faLock} />
                                <span>
                                    <b>Neactivat.</b> Se activează după confirmarea plății.{' '}
                                    Apăsați pentru a alege modalitatea de plată.
                                </span>
                            </div>
                        )}
                        {subInLastMonth && (
                            <div
                                className='status-renew'
                                onClick={() => handleOpen('subscriptions')}
                                title='Vezi abonamentele disponibile'
                            >
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                <span>
                                    <b>Ultima lună de abonament.</b>{' '}
                                    {subDaysLeft > 1
                                        ? `Mai aveți ${subDaysLeft} zile.`
                                        : subDaysLeft === 1
                                            ? 'Expiră mâine.'
                                            : 'Expiră astăzi.'}{' '}
                                    Reînnoiți-l pentru a putea genera și luna viitoare.
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Lines Usage Card */}
                <div className='status-card'>
                    <div className='status-card-header'>
                        <FontAwesomeIcon icon={faChartLine} className='status-icon' />
                        <span>Linii luna curentă</span>
                    </div>
                    <div className='status-card-body'>
                        {/* Not yet paid: these lines are an allowance, not a balance.
                            Showing them as "rămase disponibile" claims they can be spent. */}
                        <div className='lines-count'>
                            <span className={`lines-remaining ${subIsPending ? 'lines-remaining--pending' : ''}`}>
                                {formatNr(subIsPending ? linesTotal : linesRemaining)}
                            </span>
                            {!subIsPending && (
                                <>
                                <span className='lines-separator'>/</span>
                                <span className='lines-total'>{formatNr(linesTotal)}</span>
                                </>
                            )}
                        </div>
                        <div className='lines-progress-bar'>
                            <div
                                className={`lines-progress-fill ${subIsPending ? 'lines-progress-fill--pending' : ''}`}
                                style={{ width: `${subIsPending ? 100 : linesPercentRemaining}%` }}
                            />
                        </div>
                        <div className='lines-label'>
                            {subIsPending ? 'disponibile după activare' : 'rămase disponibile'}
                        </div>
                    </div>
                </div>

                {/* Features Available Card */}
                <div className='status-card'>
                    <div className='status-card-header'>
                        <FontAwesomeIcon icon={faBoxesStacked} className='status-icon' />
                        <span>Disponibile</span>
                    </div>
                    <div className='status-card-body'>
                        <div className='feature-row'>
                            <span className='feature-label'>Machete</span>
                            <span className='feature-value'>{layoutsAvailable}</span>
                        </div>
                        <div className='feature-row'>
                            <span className='feature-label'>Firme</span>
                            <span className='feature-value'>{companiesAvailable}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Groups */}
            <div className='dashboard-nav-groups'>
                {/* Declarații Group */}
                <div className='nav-group'>
                    <h3 className='nav-group-title'>Declarații</h3>
                    <div className='nav-group-items'>
                        <div className='nav-item' onClick={() => handleOpen('import')}>
                            <FontAwesomeIcon icon={faFileExport} style={{marginRight: '0.5em', opacity: 0.7}} />
                            Generează Intrastat
                        </div>
                        <div className='nav-item' onClick={() => handleOpen('generatedHistory')}>
                            <FontAwesomeIcon icon={faClockRotateLeft} style={{marginRight: '0.5em', opacity: 0.7}} />
                            Istoric declarații
                        </div>
                        <div className='nav-item' onClick={() => handleOpen('layouts')}>
                            <FontAwesomeIcon icon={faLayerGroup} style={{marginRight: '0.5em', opacity: 0.7}} />
                            Machete
                        </div>
                        <div className='nav-item' onClick={() => handleOpen('validator')}>
                            <FontAwesomeIcon icon={faFileCode} style={{marginRight: '0.5em', opacity: 0.7}} />
                            Verifică XML
                        </div>
                    </div>
                </div>

                {/* Cont Group */}
                <div className='nav-group'>
                    <h3 className='nav-group-title'>Cont</h3>
                    <div className='nav-group-items'>
                        <div className='nav-item' onClick={() => handleOpen('profile')}>
                            <FontAwesomeIcon icon={faUser} style={{marginRight: '0.5em', opacity: 0.7}} />
                            Profil
                        </div>
                        <div className='nav-item' onClick={() => handleOpen('subscriptions')}>
                            <FontAwesomeIcon icon={faCreditCard} style={{marginRight: '0.5em', opacity: 0.7}} />
                            Abonamente
                        </div>
                        <div className='nav-item' onClick={() => handleOpen('invoices')}>
                            <FontAwesomeIcon icon={faReceipt} style={{marginRight: '0.5em', opacity: 0.7}} />
                            Plăți
                        </div>
                    </div>
                </div>

                {/* Utilități Group */}
                <div className='nav-group'>
                    <h3 className='nav-group-title'>Utilități</h3>
                    <div className='nav-group-items'>
                        <div className='nav-item' onClick={() => setModal(true)}>
                            <FontAwesomeIcon icon={faMagnifyingGlass} style={{marginRight: '0.5em', opacity: 0.7}} />
                            Caută coduri Intrastat {new Date().getFullYear()}
                        </div>
                        <div className='nav-item' onClick={() => goToPage("docs")}>
                            <FontAwesomeIcon icon={faBook} style={{marginRight: '0.5em', opacity: 0.7}} />
                            Documentație
                        </div>
                    </div>
                </div>

                {/* Firme Group - only for tert users */}
                {userData.data?.tert && (
                    <div className='nav-group'>
                        <h3 className='nav-group-title'>Firme</h3>
                        <div className='nav-group-items'>
                            <div className='nav-item' onClick={() => handleOpen('companies')}>
                                <FontAwesomeIcon icon={faBuilding} style={{marginRight: '0.5em', opacity: 0.7}} />
                                Gestiune firme
                            </div>
                        </div>
                    </div>
                )}

                {/* Admin Group - only for admins */}
                {userData.data?.admin && (
                    <div className='nav-group nav-group-admin'>
                        <h3 className='nav-group-title'>Administrare</h3>
                        <div className='nav-group-items'>
                            <div className='nav-item' onClick={() => handleOpen('admin-panel')}>
                                <FontAwesomeIcon icon={faGear} style={{marginRight: '0.5em', opacity: 0.7}} />
                                Panou admin
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )

    // Render quick access bar when content is open
    const renderQuickAccessBar = () => (
        <div className='dashboard-quick-access'>
            <div className='quick-access-row'>
                <div
                    className={`quick-access-btn hero-btn ${page === 'import' ? 'active' : ''}`}
                    onClick={() => handleOpen('import')}
                >
                    {page === 'import' ? 'Reimportă' : 'Generează'}
                </div>

                <div className='quick-access-divider' />

                <div
                    className={`quick-access-btn ${page === 'generatedHistory' ? 'active' : ''}`}
                    onClick={() => handleOpen('generatedHistory')}
                >
                    Istoric
                </div>
                <div
                    className={`quick-access-btn ${page === 'layouts' ? 'active' : ''}`}
                    onClick={() => handleOpen('layouts')}
                >
                    Machete
                </div>
                <div
                    className={`quick-access-btn ${page === 'profile' ? 'active' : ''}`}
                    onClick={() => handleOpen('profile')}
                >
                    Profil
                </div>

                {userData.data?.tert && (
                    <div
                        className={`quick-access-btn ${page === 'companies' ? 'active' : ''}`}
                        onClick={() => handleOpen('companies')}
                    >
                        Firme
                    </div>
                )}

                <div
                    className={`quick-access-btn ${page === 'subscriptions' ? 'active' : ''}`}
                    onClick={() => handleOpen('subscriptions')}
                >
                    Abonamente
                </div>
                <div
                    className={`quick-access-btn ${page === 'invoices' ? 'active' : ''}`}
                    onClick={() => handleOpen('invoices')}
                >
                    Plăți
                </div>

                {userData.data?.admin && (
                    <div
                        className={`quick-access-btn admin-btn ${page === 'admin-panel' ? 'active' : ''}`}
                        onClick={() => handleOpen('admin-panel')}
                    >
                        Admin
                    </div>
                )}

                <div className='quick-access-divider' />

                <div
                    className='quick-access-btn'
                    onClick={handleClose}
                    style={{backgroundColor: 'transparent', border: '1px solid var(--faded-font-color)'}}
                >
                    ✕ Închide
                </div>
            </div>
        </div>
    )

    return (
        <div className='app-wrapper'>
            <h1 className={`main-title${windowOpen ? ' hide' : ''}`}>Program Intrastat</h1>

            {loading || !isDataReady ? (
                <FontAwesomeIcon className='spinner' icon={faSpinner}/>
            ) : (
                <>
                    {/* Dashboard or Quick Access Bar */}
                    {!windowOpen ? renderDashboard() : renderQuickAccessBar()}

                    {/* Inline Content */}
                    {windowOpen && page !== 'import' && (
                        <div className='app-section'>
                            {page === 'profile' && <Profile />}
                            {page === 'generatedHistory' && <GeneratedHistory />}
                            {/* goToPayments lets a pending subscription in the table link
                                straight to where it can be paid for */}
                            {page === 'subscriptions' && <Subscriptions goToPayments={() => handleOpen('invoices')} />}
                            {page === 'layouts' && <Layouts />}
                            {/* same component as the public /verificare-xml-intrastat page,
                                embedded here without its landing copy */}
                            {page === 'validator' && <IntrastatValidator embedded={true} />}
                            {page === 'companies' && <Companies />}
                            {page === 'invoices' && <Invoices />}
                            {page === 'admin-panel' && userData.data.admin && <AdminPanel />}
                        </div>
                    )}
                </>
            )}

            {/* Import Excel renders outside app-section */}
            {page === 'import' && <ImportExcel />}

            {/* Confirm Dialog */}
            {openConfirm &&
            <ConfirmBox
                onConfirm = {confirmData.func}
                onClose = {() => setOpenConfirm(false)}
                content = {confirmData.content}
                title = {confirmData.title}
                buttonConfirm = {confirmData.buttonConfirm}
                buttonCancel = {confirmData.buttonCancel}
            />}

            <ReactTooltip effect="solid" id='searchTT' type='info'
                getContent={(dataTip) => `${dataTip}`}/>

            {/* HS Code Search Modal */}
            {modal && <ModalSetValue
                values = {{type: 0}}
                setValue = {false}
                close = {() => setModal(false)}
            />}
        </div>
    )
}

export default MainApp
